import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { X, Upload, Palette } from 'lucide-react';
import { BrandSettings } from '../hooks/useBrandSettings';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (settings: BrandSettings) => void;
  currentSettings: BrandSettings;
}

const PRESET_COLORS = [
  '#3B82F6', // Blue
  '#EF4444', // Red
  '#10B981', // Green
  '#F59E0B', // Amber
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#F97316', // Orange
  '#6366F1', // Indigo
  '#14B8A6', // Teal
  '#DC2626', // Rose
];

// Color utility functions
function hexToHsl(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  
  return [h * 360, s * 100, l * 100];
}

function hslToHex(h: number, s: number, l: number): string {
  h /= 360;
  s /= 100;
  l /= 100;
  
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  
  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  
  const toHex = (c: number) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function getContrastColor(backgroundColor: string): string {
  const rgb = parseInt(backgroundColor.slice(1), 16);
  const r = (rgb >> 16) & 0xFF;
  const g = (rgb >> 8) & 0xFF;
  const b = (rgb) & 0xFF;
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#1E293B' : '#F8FAFC';
}

function generateThemeFromPrimary(primaryColor: string, variant: 'light' | 'medium' | 'dark'): BrandSettings {
  const [h, s, l] = hexToHsl(primaryColor);
  
  let backgroundColor: string;
  let containerColor: string;
  let cardBackgroundColor: string;
  let accentColor: string;
  let secondaryColor: string;
  
  if (variant === 'light') {
    backgroundColor = '#FFFFFF';
    containerColor = hslToHex(h, Math.max(0, s - 30), Math.min(95, l + 30));
    cardBackgroundColor = '#FFFFFF';
    accentColor = hslToHex(h, Math.max(0, s - 50), Math.min(95, l + 40));
    secondaryColor = hslToHex(h, Math.max(0, s - 20), Math.min(60, l + 10));
  } else if (variant === 'medium') {
    backgroundColor = hslToHex(h, Math.max(0, s - 40), Math.min(90, l + 20));
    containerColor = hslToHex(h, Math.max(0, s - 30), Math.min(85, l + 10));
    cardBackgroundColor = hslToHex(h, Math.max(0, s - 50), Math.min(95, l + 30));
    accentColor = hslToHex(h, Math.max(0, s - 60), Math.min(90, l + 35));
    secondaryColor = hslToHex(h, Math.max(0, s - 10), Math.min(70, l + 5));
  } else { // dark
    backgroundColor = hslToHex(h, Math.min(100, s + 20), Math.max(5, l - 40));
    containerColor = hslToHex(h, Math.min(100, s + 10), Math.max(10, l - 30));
    cardBackgroundColor = hslToHex(h, Math.min(100, s + 5), Math.max(15, l - 25));
    accentColor = hslToHex(h, Math.min(100, s + 15), Math.max(20, l - 35));
    secondaryColor = hslToHex(h, Math.min(100, s + 5), Math.max(30, l - 20));
  }
  
  return {
    primaryColor,
    secondaryColor,
    accentColor,
    backgroundColor,
    containerColor,
    textColor: getContrastColor(backgroundColor),
    cardBackgroundColor,
  };
}

const PRESET_THEMES = [
  {
    name: 'Light',
    primaryColor: '#3B82F6',
    secondaryColor: '#64748B',
    accentColor: '#F1F5F9',
    backgroundColor: '#FFFFFF',
    containerColor: '#F8FAFC',
    textColor: '#1E293B',
    cardBackgroundColor: '#FFFFFF',
  },
  {
    name: 'Dark',
    primaryColor: '#60A5FA',
    secondaryColor: '#94A3B8',
    accentColor: '#374151',
    backgroundColor: '#0F172A',
    containerColor: '#1E293B',
    textColor: '#F8FAFC',
    cardBackgroundColor: '#334155',
  },
  {
    name: 'Warm',
    primaryColor: '#F59E0B',
    secondaryColor: '#78716C',
    accentColor: '#FEF3C7',
    backgroundColor: '#FFFBEB',
    containerColor: '#FEF3C7',
    textColor: '#451A03',
    cardBackgroundColor: '#FFFFFF',
  },
  {
    name: 'Cool',
    primaryColor: '#06B6D4',
    secondaryColor: '#64748B',
    accentColor: '#E0F2FE',
    backgroundColor: '#F0F9FF',
    containerColor: '#E0F2FE',
    textColor: '#0C4A6E',
    cardBackgroundColor: '#FFFFFF',
  },
];

export function SettingsModal({ isOpen, onClose, onSave, currentSettings }: SettingsModalProps) {
  const [settings, setSettings] = useState<BrandSettings>(currentSettings);
  const [logoPreview, setLogoPreview] = useState<string | null>(currentSettings.logo || null);
  const [pasteUrl, setPasteUrl] = useState('');
  const [smartPrimary, setSmartPrimary] = useState('#3B82F6');
  const [showSmartGenerator, setShowSmartGenerator] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setLogoPreview(result);
        setSettings(prev => ({ ...prev, logo: result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePasteUrl = () => {
    if (pasteUrl.trim()) {
      // Validate if it's a valid image URL
      const img = new Image();
      img.onload = () => {
        setLogoPreview(pasteUrl);
        setSettings(prev => ({ ...prev, logo: pasteUrl }));
        setPasteUrl('');
      };
      img.onerror = () => {
        alert('Invalid image URL. Please make sure the URL points to a valid image.');
      };
      img.src = pasteUrl;
    }
  };

  const handleClipboardPaste = async (event: React.ClipboardEvent) => {
    const items = event.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = (e) => {
            const result = e.target?.result as string;
            setLogoPreview(result);
            setSettings(prev => ({ ...prev, logo: result }));
          };
          reader.readAsDataURL(file);
          break;
        }
      }
    }
  };

  const handleColorChange = (colorType: keyof BrandSettings, color: string) => {
    setSettings(prev => ({ ...prev, [colorType]: color }));
  };

  const handleSave = () => {
    onSave(settings);
    onClose();
  };

  const handleReset = () => {
    setSettings({
      primaryColor: '#3B82F6',
      secondaryColor: '#64748B',
      accentColor: '#F1F5F9',
      backgroundColor: '#FFFFFF',
      containerColor: '#F8FAFC',
      textColor: '#1E293B',
      cardBackgroundColor: '#FFFFFF',
    });
    setLogoPreview(null);
  };

  const generateSmartTheme = (variant: 'light' | 'medium' | 'dark') => {
    const generatedTheme = generateThemeFromPrimary(smartPrimary, variant);
    setSettings(generatedTheme);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Brand Settings</CardTitle>
            <CardDescription>Customize your dashboard appearance</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Logo Upload Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              <Label className="text-base font-medium">Brand Logo</Label>
            </div>
            <div className="flex items-center gap-4">
              <div 
                className="w-24 h-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50"
                onPaste={handleClipboardPaste}
                title="Paste image from clipboard here"
              >
                {logoPreview ? (
                  <img src={logoPreview} alt="Brand logo" className="max-w-full max-h-full object-contain" />
                ) : (
                  <span className="text-gray-400 text-sm">No logo</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload Logo
                </Button>
                {logoPreview && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setLogoPreview(null);
                      setSettings(prev => ({ ...prev, logo: undefined }));
                    }}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
            
            {/* URL Input Section */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Or paste image URL</Label>
              <div className="flex gap-2">
                <Input
                  value={pasteUrl}
                  onChange={(e) => setPasteUrl(e.target.value)}
                  placeholder="https://example.com/logo.png"
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePasteUrl}
                  disabled={!pasteUrl.trim()}
                >
                  Use URL
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                You can also paste images directly into the preview area above (Ctrl+V)
              </p>
            </div>
          </div>

          {/* Smart Theme Generator */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Palette className="w-5 h-5" />
                <Label className="text-base font-medium">Smart Theme Generator</Label>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSmartGenerator(!showSmartGenerator)}
              >
                {showSmartGenerator ? 'Hide' : 'Show'} Generator
              </Button>
            </div>
            
            {showSmartGenerator && (
              <div className="p-4 border rounded-lg bg-gray-50">
                <Label className="text-sm font-medium mb-2 block">Choose your primary color</Label>
                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="color"
                    value={smartPrimary}
                    onChange={(e) => setSmartPrimary(e.target.value)}
                    className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                  />
                  <Input
                    value={smartPrimary}
                    onChange={(e) => setSmartPrimary(e.target.value)}
                    className="w-24"
                    placeholder="#3B82F6"
                  />
                  <div className="flex gap-1 flex-wrap">
                    {PRESET_COLORS.slice(0, 8).map((color) => (
                      <button
                        key={color}
                        onClick={() => setSmartPrimary(color)}
                        className={`w-6 h-6 rounded border-2 ${
                          smartPrimary === color ? 'border-gray-800' : 'border-gray-300'
                        }`}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>
                
                <Label className="text-sm font-medium mb-2 block">Choose theme style</Label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => generateSmartTheme('light')}
                    className="p-3 border rounded-lg hover:border-gray-400 transition-colors text-left"
                  >
                    <div className="text-sm font-medium mb-1">Light Theme</div>
                    <div className="text-xs text-gray-600 mb-2">Clean white background</div>
                    <div className="flex gap-1">
                      <div className="w-4 h-4 rounded bg-white border"></div>
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: smartPrimary }}></div>
                      <div className="w-4 h-4 rounded bg-gray-100 border"></div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => generateSmartTheme('medium')}
                    className="p-3 border rounded-lg hover:border-gray-400 transition-colors text-left"
                  >
                    <div className="text-sm font-medium mb-1">Medium Theme</div>
                    <div className="text-xs text-gray-600 mb-2">Subtle colored background</div>
                    <div className="flex gap-1">
                      <div className="w-4 h-4 rounded border" style={{ backgroundColor: generateThemeFromPrimary(smartPrimary, 'medium').backgroundColor }}></div>
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: smartPrimary }}></div>
                      <div className="w-4 h-4 rounded border" style={{ backgroundColor: generateThemeFromPrimary(smartPrimary, 'medium').containerColor }}></div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => generateSmartTheme('dark')}
                    className="p-3 border rounded-lg hover:border-gray-400 transition-colors text-left"
                  >
                    <div className="text-sm font-medium mb-1">Dark Theme</div>
                    <div className="text-xs text-gray-600 mb-2">Rich dark background</div>
                    <div className="flex gap-1">
                      <div className="w-4 h-4 rounded border" style={{ backgroundColor: generateThemeFromPrimary(smartPrimary, 'dark').backgroundColor }}></div>
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: smartPrimary }}></div>
                      <div className="w-4 h-4 rounded border" style={{ backgroundColor: generateThemeFromPrimary(smartPrimary, 'dark').containerColor }}></div>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Color Customization Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              <Label className="text-base font-medium">Manual Color Customization</Label>
            </div>
            
            {/* Primary Color */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Primary Color</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.primaryColor}
                  onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                  className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                />
                <Input
                  value={settings.primaryColor}
                  onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                  className="w-24"
                  placeholder="#3B82F6"
                />
                <div className="flex gap-1 flex-wrap">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      onClick={() => handleColorChange('primaryColor', color)}
                      className={`w-6 h-6 rounded border-2 ${
                        settings.primaryColor === color ? 'border-gray-800' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Secondary Color */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Secondary Color</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.secondaryColor}
                  onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                  className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                />
                <Input
                  value={settings.secondaryColor}
                  onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                  className="w-24"
                  placeholder="#64748B"
                />
                <div className="flex gap-1 flex-wrap">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      onClick={() => handleColorChange('secondaryColor', color)}
                      className={`w-6 h-6 rounded border-2 ${
                        settings.secondaryColor === color ? 'border-gray-800' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Accent Color */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Accent Color</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.accentColor}
                  onChange={(e) => handleColorChange('accentColor', e.target.value)}
                  className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                />
                <Input
                  value={settings.accentColor}
                  onChange={(e) => handleColorChange('accentColor', e.target.value)}
                  className="w-24"
                  placeholder="#F1F5F9"
                />
                <div className="flex gap-1 flex-wrap">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      onClick={() => handleColorChange('accentColor', color)}
                      className={`w-6 h-6 rounded border-2 ${
                        settings.accentColor === color ? 'border-gray-800' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Theme Colors Section */}
            <div className="pt-4 border-t border-gray-200">
              <Label className="text-sm font-medium text-gray-700">Theme Colors</Label>
              <p className="text-xs text-gray-500 mb-4">Customize the overall page appearance</p>
              
              {/* Preset Themes */}
              <div className="mb-4">
                <Label className="text-xs font-medium text-gray-600 mb-2 block">Quick Themes</Label>
                <div className="grid grid-cols-4 gap-2">
                  {PRESET_THEMES.map((theme) => (
                    <button
                      key={theme.name}
                      onClick={() => setSettings(theme)}
                      className="p-2 border rounded-lg hover:border-gray-400 transition-colors"
                      style={{
                        backgroundColor: theme.cardBackgroundColor,
                        borderColor: theme.containerColor,
                      }}
                    >
                      <div 
                        className="w-full h-8 rounded mb-1"
                        style={{ backgroundColor: theme.backgroundColor }}
                      />
                      <div className="text-xs font-medium" style={{ color: theme.textColor }}>
                        {theme.name}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Background Color */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Background Color</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.backgroundColor}
                  onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
                  className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                />
                <Input
                  value={settings.backgroundColor}
                  onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
                  className="w-24"
                  placeholder="#FFFFFF"
                />
                <div className="flex gap-1 flex-wrap">
                  {['#FFFFFF', '#F8FAFC', '#F1F5F9', '#E2E8F0', '#1E293B', '#0F172A'].map((color) => (
                    <button
                      key={color}
                      onClick={() => handleColorChange('backgroundColor', color)}
                      className={`w-6 h-6 rounded border-2 ${
                        settings.backgroundColor === color ? 'border-gray-800' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Container Color */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Container Color</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.containerColor}
                  onChange={(e) => handleColorChange('containerColor', e.target.value)}
                  className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                />
                <Input
                  value={settings.containerColor}
                  onChange={(e) => handleColorChange('containerColor', e.target.value)}
                  className="w-24"
                  placeholder="#F8FAFC"
                />
                <div className="flex gap-1 flex-wrap">
                  {['#F8FAFC', '#F1F5F9', '#E2E8F0', '#CBD5E1', '#334155', '#475569'].map((color) => (
                    <button
                      key={color}
                      onClick={() => handleColorChange('containerColor', color)}
                      className={`w-6 h-6 rounded border-2 ${
                        settings.containerColor === color ? 'border-gray-800' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Card Background Color */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Card Background</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.cardBackgroundColor}
                  onChange={(e) => handleColorChange('cardBackgroundColor', e.target.value)}
                  className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                />
                <Input
                  value={settings.cardBackgroundColor}
                  onChange={(e) => handleColorChange('cardBackgroundColor', e.target.value)}
                  className="w-24"
                  placeholder="#FFFFFF"
                />
                <div className="flex gap-1 flex-wrap">
                  {['#FFFFFF', '#F8FAFC', '#F1F5F9', '#E2E8F0', '#334155', '#475569'].map((color) => (
                    <button
                      key={color}
                      onClick={() => handleColorChange('cardBackgroundColor', color)}
                      className={`w-6 h-6 rounded border-2 ${
                        settings.cardBackgroundColor === color ? 'border-gray-800' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Text Color */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Text Color</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.textColor}
                  onChange={(e) => handleColorChange('textColor', e.target.value)}
                  className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                />
                <Input
                  value={settings.textColor}
                  onChange={(e) => handleColorChange('textColor', e.target.value)}
                  className="w-24"
                  placeholder="#1E293B"
                />
                <div className="flex gap-1 flex-wrap">
                  {['#1E293B', '#334155', '#475569', '#64748B', '#FFFFFF', '#F1F5F9'].map((color) => (
                    <button
                      key={color}
                      onClick={() => handleColorChange('textColor', color)}
                      className={`w-6 h-6 rounded border-2 ${
                        settings.textColor === color ? 'border-gray-800' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Preview</Label>
            <div 
              className="p-4 border rounded-lg"
              style={{ 
                backgroundColor: settings.cardBackgroundColor,
                borderColor: settings.containerColor
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Preview" className="h-8 object-contain" />
                  ) : (
                    <div 
                      className="h-8 w-8 rounded flex items-center justify-center"
                      style={{ backgroundColor: settings.containerColor }}
                    >
                      <span 
                        className="text-xs"
                        style={{ color: settings.textColor }}
                      >
                        Logo
                      </span>
                    </div>
                  )}
                  <span 
                    className="font-semibold" 
                    style={{ color: settings.primaryColor }}
                  >
                    Your Brand
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="w-8 h-8 rounded-full border-2"
                    style={{ 
                      backgroundColor: settings.accentColor,
                      borderColor: settings.containerColor
                    }}
                  />
                  <Button
                    size="sm"
                    style={{
                      backgroundColor: settings.primaryColor,
                      borderColor: settings.primaryColor,
                      color: '#FFFFFF'
                    }}
                  >
                    Sample Button
                  </Button>
                </div>
              </div>
              
              {/* Sample card preview */}
              <div 
                className="mt-3 p-3 rounded border"
                style={{ 
                  backgroundColor: settings.backgroundColor,
                  borderColor: settings.containerColor
                }}
              >
                <div 
                  className="text-sm font-medium mb-1"
                  style={{ color: settings.textColor }}
                >
                  Sample Card
                </div>
                <div 
                  className="text-xs"
                  style={{ color: settings.secondaryColor }}
                >
                  This shows how cards will look with your theme
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={handleReset}>
              Reset to Default
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                Save Changes
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
